import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { canUseModel, getModelPlan, type Plan } from '../../lib/plans'
import { useAuthStore } from '../../auth/useAuthStore'
import type { AIMode, AIModel, ChatMessage } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'
import { useVoiceStore, startVoiceSession, stopVoiceSession, isVoiceSupported, speakText, startWakeWordListening, stopWakeWordListening, isWakeWordActive, setLastAIResponse, isSpeaking, getAudioLevel, stopTTS } from '../../services/voice'
import { getInstalledPrompts } from '../../services/extensionRuntime'
import { ChatImageButton } from './ChatImageUpload'
import { AudioOrb } from './AudioOrb'
import { usePulseMemory } from '../PulseMemory/usePulseMemory'
import { useAgentPermissions } from '../AgentPermissions/useAgentPermissions'
import { AgentTimeline } from '../AgentTimeline'
import { AgentStreamRenderer, type AgentPhase } from './AgentStreamRenderer'
import { AgentActivityLog } from './AgentActivityLog'
import { TerminalCard } from './TerminalCard'


const AI_MODELS: { id: AIModel; name: string; provider: string; icon: string; active: boolean; category: 'coding' | 'images' | 'both' }[] = [
  { id: 'auto', name: 'Auto ⭐', provider: 'Smart Routing', icon: '✨', active: true, category: 'coding' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'Google', icon: '✦', active: true, category: 'coding' },
  { id: 'gemini-2.5-pro',   name: 'Gemini 2.5 Pro',   provider: 'Google', icon: '✦', active: false, category: 'coding' },
  { id: 'gpt-4o',           name: 'GPT-4o',            provider: 'OpenAI', icon: '◉', active: false, category: 'both' },
  { id: 'gpt-4o-mini',      name: 'GPT-4o Mini',       provider: 'OpenAI', icon: '◉', active: false, category: 'coding' },
  { id: 'claude-4-sonnet',  name: 'Claude 4 Sonnet',   provider: 'Anthropic', icon: '◈', active: false, category: 'coding' },
  { id: 'claude-4-opus',    name: 'Claude 4 Opus',     provider: 'Anthropic', icon: '◈', active: false, category: 'coding' },
  { id: 'deepseek-chat',    name: 'DeepSeek Chat',     provider: 'DeepSeek', icon: '◆', active: false, category: 'coding' },
  { id: 'llama-4',          name: 'Llama 4',            provider: 'Meta', icon: '🦙', active: false, category: 'coding' },
  { id: 'grok-3',           name: 'Grok 3',             provider: 'xAI', icon: '✕', active: false, category: 'coding' },
  { id: 'openai/gpt-4o',    name: 'GPT-4o (OpenRouter)', provider: 'OpenRouter', icon: '🔀', active: false, category: 'both' },
  { id: 'anthropic/claude-3.5-sonnet-20240620', name: 'Claude 3.5 (OpenRouter)', provider: 'OpenRouter', icon: '🔀', active: false, category: 'coding' },
  // Free OpenRouter models
  { id: 'nvidia/nemotron-3-ultra-550b-a55b:free', name: 'Nemotron 3 Ultra 550B', provider: 'NVIDIA (Free)', icon: '🟢', active: true, category: 'coding' },
  { id: "pollinations-ai" as any, name: "Pollinations AI", provider: "Free - No Key", icon: "🎨", active: true, category: "images" },
  { id: 'poolside/laguna-m.1:free', name: 'Laguna M.1', provider: 'Poolside (Free)', icon: '🟢', active: true, category: 'coding' },
  { id: 'poolside/laguna-xs.2:free', name: 'Laguna XS.2', provider: 'Poolside (Free)', icon: '🟢', active: true, category: 'coding' },
  { id: 'mistral-medium-3.5', name: 'Mistral Medium 3.5', provider: 'Mistral', icon: '🟣', active: true, category: 'coding' },
  { id: 'openai/gpt-oss-120b:free', name: 'GPT-OSS 120B', provider: 'OpenAI (Free)', icon: '🟢', active: true, category: 'coding' },
  { id: 'openai/gpt-oss-20b:free', name: 'GPT-OSS 20B', provider: 'OpenAI (Free)', icon: '🟢', active: true, category: 'coding' },
  { id: 'meta-llama/llama-3.2-3b-instruct:free', name: 'Llama 3.2 3B', provider: 'Meta (Free)', icon: '🦙', active: true, category: 'coding' },
  { id: 'openrouter/owl-alpha', name: 'Owl Alpha', provider: 'OpenRouter', icon: '🦉', active: true, category: 'coding' },
  // Amazon Bedrock models
  { id: 'bedrock-claude-sonnet', name: 'Claude Sonnet (Bedrock)', provider: 'AWS Bedrock', icon: '🔶', active: true, category: 'coding' },
  { id: 'bedrock-claude-haiku', name: 'Claude Haiku (Bedrock)', provider: 'AWS Bedrock', icon: '🔶', active: true, category: 'coding' },
  { id: 'bedrock-llama-4', name: 'Llama 4 (Bedrock)', provider: 'AWS Bedrock', icon: '🔶', active: true, category: 'coding' },
  { id: 'bedrock-mistral', name: 'Mistral Large (Bedrock)', provider: 'AWS Bedrock', icon: '🔶', active: true, category: 'coding' },
  { id: 'bedrock-titan', name: 'Amazon Titan (Bedrock)', provider: 'AWS Bedrock', icon: '🔶', active: true, category: 'coding' },
]
interface HistoryConv { id: string; title: string; mode: string; model: string; created_at: string; updated_at: string }

export function AIPanel({ onOpenGoals }: { onOpenGoals?: () => void }) {
  const {
    aiMode, setAIMode,
    aiModel, setAIModel,
    chatMessages, isAIThinking,
    contextPills, addContextPill, removeContextPill,
    isVoiceActive, toggleVoice,
    addMessage, setAIThinking,
  } = useAppStore()

    const pulseMemory = usePulseMemory((s) => s.memory)
  const _setPulseName = usePulseMemory((s) => s.setUserName)

const [input, setInput] = useState('')
  const [streamingText, setStreamingText] = useState('')
  const [summaryText, setSummaryText] = useState('')
  const [showModelPicker, setShowModelPicker] = useState(false)
  const [imageMode, setImageMode] = useState(false)
  const [agentEvents, setAgentEvents] = useState<any[]>([])
  const [agentRunning, setAgentRunning] = useState(false)
  const [agentGoal, setAgentGoal] = useState('')
  const [terminalCards, setTerminalCards] = useState<Array<{ id: string; command: string; output: string | null }>>([])
  const terminalCardsRef = useRef<Array<{ id: string; command: string; output: string | null }>>([])
  const [agentArchive, setAgentArchive] = useState<Array<{ goal: string; phases: AgentPhase[]; filesModified: string[] }>>(() => {
    try { return JSON.parse(localStorage.getItem('pulse-agent-archive') ?? '[]') } catch { return [] }
  })
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const voiceStoppingRef = useRef(false)
  const modelPickerRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // History state
  const [showHistory, setShowHistory] = useState(false)
  const [showActivityLog, setShowActivityLog] = useState(false)
  const [historyList, setHistoryList] = useState<HistoryConv[]>([])
  const [convId, setConvId] = useState<string | null>(null)
  const convIdRef = useRef<string | null>(null)
  convIdRef.current = convId

  // Chat tabs state
  interface ChatTab { id: string; title: string; convId: string | null; messages: any[] }
  const [chatTabs, setChatTabs] = useState<ChatTab[]>([{ id: 'default', title: 'New Chat', convId: null, messages: [] }])
  const [activeTabIdx, setActiveTabIdx] = useState(0)

  const switchTab = useCallback((idx: number) => {
    // Save current messages to current tab
    setChatTabs(prev => {
      const updated = [...prev]
      updated[activeTabIdx] = { ...updated[activeTabIdx], messages: [...useAppStore.getState().chatMessages], convId: convIdRef.current }
      return updated
    })
    // Load target tab
    setActiveTabIdx(idx)
    setTimeout(() => {
      setChatTabs(prev => {
        const tab = prev[idx]
        if (tab) {
          useAppStore.setState({ chatMessages: tab.messages })
          setConvId(tab.convId)
          convIdRef.current = tab.convId
        }
        return prev
      })
    }, 0)
  }, [activeTabIdx])

  const addChatTab = useCallback(() => {
    // Save current tab
    setChatTabs(prev => {
      const updated = [...prev]
      updated[activeTabIdx] = { ...updated[activeTabIdx], messages: [...useAppStore.getState().chatMessages], convId: convIdRef.current }
      const newTab: ChatTab = { id: crypto.randomUUID(), title: 'New Chat', convId: null, messages: [] }
      return [...updated, newTab]
    })
    setActiveTabIdx(prev => {
      const newIdx = chatTabs.length
      setTimeout(() => {
        useAppStore.setState({ chatMessages: [] })
        setConvId(null)
        convIdRef.current = null
      }, 0)
      return newIdx
    })
  }, [activeTabIdx, chatTabs.length])

  const closeChatTab = useCallback((idx: number) => {
    if (chatTabs.length <= 1) return // keep at least one tab
    setChatTabs(prev => {
      const updated = prev.filter((_, i) => i !== idx)
      return updated
    })
    const newIdx = idx >= chatTabs.length - 1 ? Math.max(0, idx - 1) : idx
    setActiveTabIdx(newIdx)
    setTimeout(() => {
      setChatTabs(prev => {
        const tab = prev[newIdx]
        if (tab) {
          useAppStore.setState({ chatMessages: tab.messages })
          setConvId(tab.convId)
          convIdRef.current = tab.convId
        }
        return prev
      })
    }, 0)
  }, [chatTabs.length])

  // Save tabs to localStorage whenever they change (for current project)
  useEffect(() => {
    const project = activeRunProjectRef.current
    if (!project) return
    const tabsToSave = chatTabs.map((tab, idx) => 
      idx === activeTabIdx 
        ? { ...tab, messages: [...chatMessages], convId: convIdRef.current }
        : tab
    )
    localStorage.setItem(`pulse-chat-tabs-${project}`, JSON.stringify(tabsToSave))
  }, [chatTabs, chatMessages, activeTabIdx])

  // Load history list
  const loadHistory = useCallback(() => {
    authFetch(apiUrl('/api/history')).then((r) => r.json()).then((data) => {
      setHistoryList(Array.isArray(data) ? data : [])
    }).catch(() => setHistoryList([]))
  }, [])

  // Load history list on mount (don't auto-restore — chat stays empty until an app is run)
  useEffect(() => {
    loadHistory()
  }, [])

  // Auto-sync user name to Pulse memory
  useEffect(() => { const name = useAuthStore.getState().user?.name; if (name && name !== pulseMemory.userName) _setPulseName(name) }, [])

  // Listen for skill replay / resume events
  useEffect(() => {
    const handler = (e: Event) => {
      const { goal } = (e as CustomEvent).detail || {}
      if (goal) {
        addMessage({ role: 'user', content: goal })
        setInput(goal)
        setTimeout(() => {
          const btn = document.querySelector('.ai-send-btn') as HTMLButtonElement
          if (btn) btn.click()
        }, 200)
      }
    }
    window.addEventListener('pulse:agent-resume', handler)
    return () => window.removeEventListener('pulse:agent-resume', handler)
  }, [])

  // Listen for active project changes (run/stop) to scope chat
  const [activeRunProject, setActiveRunProject] = useState<string | null>(null)
  const activeRunProjectRef = useRef<string | null>(null)
  useEffect(() => {
    const handler = (e: Event) => {
      const { project } = (e as CustomEvent).detail || {}
      setActiveRunProject(project || null)
      activeRunProjectRef.current = project || null
      if (!project) {
        // Stopped — clear everything to empty state
        setConvId(null)
        convIdRef.current = null
        useAppStore.setState({ chatMessages: [] })
        setChatTabs([{ id: crypto.randomUUID(), title: 'New Chat', convId: null, messages: [] }])
        setActiveTabIdx(0)
        return
      }
      // Running an app — restore saved tabs or load from history
      console.log('[chat] Loading history for project:', project)
      // Check if we have saved tabs for this project
      const savedKey = `pulse-chat-tabs-${project}`
      const savedTabs = localStorage.getItem(savedKey)
      if (savedTabs) {
        try {
          const tabs = JSON.parse(savedTabs) as ChatTab[]
          if (tabs.length > 0) {
            setChatTabs(tabs)
            setActiveTabIdx(0)
            useAppStore.setState({ chatMessages: tabs[0].messages })
            setConvId(tabs[0].convId)
            convIdRef.current = tabs[0].convId
            return
          }
        } catch {}
      }
      // No saved tabs — load from history API
      authFetch(apiUrl(`/api/history?project=${encodeURIComponent(project)}`))
        .then(r => r.json())
        .then(async (data) => {
          const list = Array.isArray(data) ? data : []
          if (list.length === 0) {
            setConvId(null)
            convIdRef.current = null
            useAppStore.setState({ chatMessages: [] })
            setChatTabs([{ id: crypto.randomUUID(), title: 'New Chat', convId: null, messages: [] }])
            setActiveTabIdx(0)
            return
          }
          // Merge all project conversations into one message list (oldest first)
          const allMessages: any[] = []
          for (const conv of [...list].reverse()) {
            try {
              const res = await authFetch(apiUrl(`/api/history/${conv.id}`))
              const detail = await res.json()
              const msgs = (detail.messages ?? []).map((m: any) => ({
                id: m.id ?? crypto.randomUUID(), role: m.role, content: m.content
              }))
              allMessages.push(...msgs)
            } catch {}
          }
          const latestConvId = list[0].id
          const mainTab: ChatTab = { id: crypto.randomUUID(), title: list[0].title || 'Chat', convId: latestConvId, messages: allMessages }
          const newTab: ChatTab = { id: crypto.randomUUID(), title: 'New Chat', convId: null, messages: [] }
          setChatTabs([mainTab, newTab])
          setActiveTabIdx(0)
          useAppStore.setState({ chatMessages: allMessages })
          setConvId(latestConvId)
          convIdRef.current = latestConvId
        }).catch(() => {
          setConvId(null)
          convIdRef.current = null
          useAppStore.setState({ chatMessages: [] })
          setChatTabs([{ id: crypto.randomUUID(), title: 'New Chat', convId: null, messages: [] }])
          setActiveTabIdx(0)
        })
    }
    window.addEventListener('pulse:active-project-changed', handler)
    return () => window.removeEventListener('pulse:active-project-changed', handler)
  }, [])

  // Listen for terminal errors and show "Fix this" suggestion
  const [terminalError, setTerminalError] = useState<any>(null)
  useEffect(() => {
    const handler = (e: Event) => {
      const error = (e as CustomEvent).detail
      setTerminalError(error)
      // Auto-clear after 30 seconds
      setTimeout(() => setTerminalError(null), 30000)
    }
    window.addEventListener('pulse:terminal-error', handler)
    return () => window.removeEventListener('pulse:terminal-error', handler)
  }, [])

  const handleFixError = async () => {
    if (!terminalError) return
    const prompt = `Fix this ${terminalError.type} error:\n\n\`\`\`\n${terminalError.fullOutput}\n\`\`\`${terminalError.file ? `\nFile: ${terminalError.file}` : ''}`
    setTerminalError(null)
    // Send as agent message
    addMessage({ role: 'user', content: prompt })
    setAIThinking(true)
  }

  // Handle undo link clicks for agent rollback
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('/undo/')) {
        e.preventDefault()
        const sessionId = target.getAttribute('href')!.replace('/undo/', '')
        authFetch(apiUrl('/api/agent-stream/rollback-session'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        }).then(r => r.json()).then(data => {
          if (data.ok) {
            addMessage({ role: 'assistant', content: `↩ Rolled back: ${data.restored?.length || 0} files restored, ${data.deleted?.length || 0} files deleted` })
            window.dispatchEvent(new CustomEvent('pulse:files-changed'))
          }
        }).catch(() => {})
      }
      // Handle save skill links
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('/save-skill/')) {
        e.preventDefault()
        const sessionId = target.getAttribute('href')!.replace('/save-skill/', '')
        const name = prompt('Name this skill (e.g. "Add footer to HTML page"):')
        if (!name) return
        authFetch(apiUrl('/api/agent-stream/skills/save'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, name }),
        }).then(r => r.json()).then(data => {
          if (data.ok) {
            addMessage({ role: 'assistant', content: `✓ Skill "${name}" saved! You can replay it from the Activity panel.` })
          }
        }).catch(() => {})
      }
      // Handle open file links
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('/open/')) {
        e.preventDefault()
        const filePath = target.getAttribute('href')!.replace('/open/', '')
        const store = useAppStore.getState()
        const sp = `/${filePath}`
        // Check if we have patch data (old + new content) for diff view
        const patch = store.agentFilePatches[sp] || store.agentFilePatches[filePath]
        if (patch && patch.oldContent && patch.newContent) {
          // Open as diff tab showing old vs new with colors
          const diffTabId = `${filePath}::diff`
          const existing = store.openTabs.find(t => t.id === diffTabId)
          if (existing) {
            store.setActiveTab(diffTabId)
          } else {
            const oldLines = patch.oldContent.split('\n')
            const newLines = patch.newContent.split('\n')
            const diffContent = buildUnifiedDiff(oldLines, newLines)
            const name = (filePath.split('/').pop() || filePath) + ' (changes)'
            store.openTab({ id: diffTabId, path: sp, name, language: 'diff', content: diffContent })
            store.setActiveTab(diffTabId)
          }
        } else {
          // No patch data — just open the file normally
          const existing = store.openTabs.find(t => t.path === sp || t.path === filePath || t.path.endsWith(filePath))
          if (existing) {
            store.setActiveTab(existing.id)
          } else {
            authFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(filePath)}`))
              .then(r => r.ok ? r.json() : null)
              .then(data => {
                if (data) {
                  const name = filePath.split('/').pop() || filePath
                  store.openTab({ id: filePath, path: sp, name, language: name.split('.').pop() || 'text', content: data.content })
                  store.setActiveTab(filePath)
                }
              }).catch(() => {})
          }
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('pulse:agent-file-patched', { detail: { path: sp } }))
            window.dispatchEvent(new CustomEvent('pulse:agent-file-patched', { detail: { path: filePath } }))
          }, 200)
        }
      }
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  // Save a message to the server
  const persistLock = useRef<Promise<void>>(Promise.resolve())
  const persistMessage = useCallback(async (role: string, content: string) => {
    // Serialize calls to prevent race conditions when creating conversations
    const prev = persistLock.current
    const task = (async () => {
      await prev
      let id = convIdRef.current
      if (!id) {
        try {
          const res = await authFetch(apiUrl('/api/history'), {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: content.slice(0, 60), mode: aiMode, model: aiModel, project: activeRunProjectRef.current }),
          })
          const data = await res.json()
          id = data.id
          setConvId(id)
          convIdRef.current = id
        } catch { return }
      }
      await authFetch(apiUrl(`/api/history/${id}/messages`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, content }),
      }).catch(() => {})
    })()
    persistLock.current = task.catch(() => {})
    await task
  }, [aiMode, aiModel])

  // Load a conversation from history into a new tab
  const loadConversation = useCallback(async (id: string) => {
    try {
      const res = await authFetch(apiUrl(`/api/history/${id}`))
      const data = await res.json()
      const messages = (data.messages ?? []).map((m: any) => ({
        id: m.id ?? crypto.randomUUID(),
        role: m.role,
        content: m.content,
        timestamp: new Date(m.created_at).getTime(),
      }))
      // Save current tab first
      setChatTabs(prev => {
        const updated = [...prev]
        updated[activeTabIdx] = { ...updated[activeTabIdx], messages: [...useAppStore.getState().chatMessages], convId: convIdRef.current }
        // Check if this conversation is already open in a tab
        const existingIdx = updated.findIndex(t => t.convId === id)
        if (existingIdx >= 0) {
          setActiveTabIdx(existingIdx)
          useAppStore.setState({ chatMessages: updated[existingIdx].messages })
          setConvId(updated[existingIdx].convId)
          return updated
        }
        // Open in a new tab
        const title = data.title || messages[0]?.content?.slice(0, 30) || 'Chat'
        const newTab = { id: crypto.randomUUID(), title, convId: id, messages }
        const newTabs = [...updated, newTab]
        setTimeout(() => {
          setActiveTabIdx(newTabs.length - 1)
          useAppStore.setState({ chatMessages: messages })
          setConvId(id)
          convIdRef.current = id
        }, 0)
        return newTabs
      })
      setShowHistory(false)
    } catch {}
  }, [activeTabIdx])

  // New chat — creates a new tab
  const newChat = useCallback(() => {
    addChatTab()
    setShowHistory(false)
    loadHistory()
  }, [addChatTab, loadHistory])

  // Delete conversation
  const deleteConv = useCallback(async (id: string) => {
    await authFetch(apiUrl(`/api/history/${id}`), { method: 'DELETE' }).catch(() => {})
    if (convId === id) newChat()
    loadHistory()
  }, [convId, newChat, loadHistory])

  // Voice store
  const voiceState = useVoiceStore((s) => s.state)
  const voiceTranscript = useVoiceStore((s) => s.transcript)
  const voiceInterim = useVoiceStore((s) => s.interimTranscript)
  const voiceSilence = useVoiceStore((s) => s.silenceMs)
  const [wakeActive, setWakeActive] = useState(false)
  const [audioLevel, setAudioLevel] = useState(0)
  useEffect(() => {
    if (voiceState === 'idle') { setAudioLevel(0); return }
    const interval = setInterval(() => {
      if (window.speechSynthesis?.speaking) {
        // TTS is reading — simulate organic pulsing
        const t = Date.now() / 1000
        setAudioLevel(0.4 + Math.sin(t * 4) * 0.2 + Math.sin(t * 7) * 0.15 + Math.sin(t * 11) * 0.1)
      } else {
        // Real mic audio level
        setAudioLevel(getAudioLevel())
      }
    }, 30)
    return () => clearInterval(interval)
  }, [voiceState])

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, streamingText, isAIThinking])

  // Close model picker on click outside
  useEffect(() => {
    if (!showModelPicker) return
    const handler = (e: MouseEvent) => {
      if (modelPickerRef.current && !modelPickerRef.current.contains(e.target as Node)) {
        setShowModelPicker(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showModelPicker])

  // Auto-apply all file operations found in an AI response
  const autoApplyFileOps = useCallback(async (responseText: string) => {
    const codeBlocks = extractCodeBlocks(responseText)
    const mode = useAppStore.getState().aiMode
    for (const block of codeBlocks) {
      // Handle terminal commands (// FILE: __terminal__) — ALWAYS execute
      if (block.trim().startsWith('// FILE: __terminal__')) {
        const commands = block.split('\n').slice(1).filter((l) => l.trim() && !l.trim().startsWith('//')).join(' && ')
        if (commands) {
          // Use HTTP API to run terminal commands
          const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
          fetch('http://localhost:3001/api/terminal/run', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ command: commands }),
          }).catch(() => {})
          // Also dispatch event for terminal panel display
          window.dispatchEvent(new CustomEvent('pulse:run-terminal', { detail: { commands } }))
          setTimeout(() => window.dispatchEvent(new CustomEvent('pulse:files-changed')), 10000)
          setTimeout(() => window.dispatchEvent(new CustomEvent('pulse:files-changed')), 20000)
          setTimeout(() => window.dispatchEvent(new CustomEvent('pulse:files-changed')), 30000)
        }
        continue
      }

      // File operations — only auto-apply in edit/agent mode
      // auto-apply in all modes

      const op = parsePulseFileOp(block)
      console.log("[auto-apply]", op ? `Writing: ${op.relPath}` : "No FILE header in block:", block.substring(0,60))
      if (!op) continue

      // Auto-apply: write to editor tab + disk
      console.log("[auto-apply] Writing:", op.relPath)
      const existingTab = useAppStore.getState().openTabs.find((t) => t.path === op.storedPath)
      if (existingTab) useAppStore.getState().snapshotFile(op.storedPath, existingTab.content)

      if (op.action === 'delete') {
        if (existingTab) useAppStore.getState().closeTab(existingTab.id)
      } else {
        if (existingTab) {
          useAppStore.getState().markDirty(existingTab.id, op.content)
        } else {
          useAppStore.getState().openTab({
            id: op.storedPath,
            path: op.storedPath,
            name: op.storedPath.split('/').at(-1) ?? op.storedPath,
            language: inferLanguageFromRelPath(op.relPath),
            content: op.content,
          })
        }
      }

      // Write to server filesystem
      try {
        const endpoint = op.action === 'delete' ? '/api/files/delete' : '/api/files/write'
        const writeRes = await authFetch(apiUrl(endpoint), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: op.relPath, content: op.content }),
        })
        if (writeRes.ok) {
          console.log("[auto-apply] ✓ Saved:", op.relPath)
        } else {
          console.error("[auto-apply] ✗ FAILED:", op.relPath, writeRes.status, await writeRes.text().catch(()=>""))
        }
      } catch (e) { console.error("[auto-apply] ERROR:", op.relPath, e) }
    }
    if (codeBlocks.some((b) => parsePulseFileOp(b))) {
      window.dispatchEvent(new CustomEvent('pulse:files-changed'))
    }
  }, [])

  // Fetch workspace files to send as context — always includes the active file
  const fetchWorkspaceFiles = useCallback(async () => {
    const files: { path: string; content: string }[] = []

    // 1. Always include the currently open/active file with full content
    const { openTabs, activeTabId } = useAppStore.getState()
    const activeTab = openTabs.find((t) => t.id === activeTabId)
    if (activeTab) {
      files.push({ path: activeTab.path.replace(/^\//, ''), content: activeTab.content })
    }
    // Also include other open tabs
    for (const tab of openTabs) {
      if (tab.id === activeTabId) continue
      files.push({ path: tab.path.replace(/^\//, ''), content: tab.content.slice(0, 2000) })
    }

    // 2. Fetch the file tree so AI knows the project structure
    try {
      const res = await authFetch(apiUrl('/api/files/list'))
      if (!res.ok) return files
      const tree = await res.json()
      const allPaths: string[] = []
      const collectPaths = (nodes: any[], prefix = '') => {
        for (const n of nodes) {
          const p = prefix ? `${prefix}/${n.name}` : n.name
          allPaths.push(n.type === 'folder' ? `${p}/` : p)
          if (n.children) collectPaths(n.children, p)
        }
      }
      collectPaths(tree)
      // Add a virtual file that describes the project structure
      files.push({ path: '__project_tree__', content: allPaths.join('\n') })
    } catch {}

    return files
  }, [])

  // On mount, check if there's a pending prompt from the landing page
  useEffect(() => {
    const prompt = localStorage.getItem('pulse-pending-prompt')
    if (!prompt) return
    localStorage.removeItem('pulse-pending-prompt')

    addMessage({ role: 'user', content: prompt })
    persistMessage('user', prompt)
    setAIThinking(true)
    ;(async () => {
      const contextFiles = await fetchWorkspaceFiles()
      try {
        const res = await authFetch(apiUrl('/api/chat/stream'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [{ role: 'user', content: prompt }],
            context: { mode: aiMode, model: aiModel, files: contextFiles, memory: pulseMemory },
          }),
        })
        if (!res.ok || !res.body) throw new Error(`Server error ${res.status}`)
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let full = ''
        let buf = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const lines = buf.split('\n')
          buf = lines.pop() ?? ''
          for (const line of lines) {
            const t = line.trim()
            if (!t.startsWith('data: ')) continue
            try {
              const data = JSON.parse(t.slice(6))
              if (data.ping) continue
              if (data.text) { full += data.text; setStreamingText(full) }
              if (data.done) {
                addMessage({ role: 'assistant', content: full })
                persistMessage('assistant', full)
                window.dispatchEvent(new CustomEvent('pulse:approve-files', { detail: { content: full } }))
                setStreamingText('')
                full = ''
              }
            } catch {}
          }
        }
        if (full.trim() || terminalCards.length > 0) {
          // Build final message: terminals first, then summary
          let finalContent = ''
          if (terminalCardsRef.current.length > 0) {
            for (const card of terminalCardsRef.current) {
              finalContent += '```shell\n' + card.command + '\n' + (card.output || '') + '\n```\n'
            }
            finalContent += '\n'
          }
          finalContent += full
          // Save: permissions+investigating + terminals + summary
          const invMarker = '*Investigating...*'
          const invPos = full.indexOf(invMarker)
          let earlyPart = full, summaryPart = ''
          if (invPos >= 0) {
            const nl = full.indexOf('\n', invPos + invMarker.length)
            if (nl >= 0) { earlyPart = full.slice(0, nl + 1); summaryPart = full.slice(nl + 1) }
          }
          const savedMsg = earlyPart + finalContent + summaryPart
          addMessage({ role: 'assistant', content: savedMsg })
          persistMessage('assistant', savedMsg)
          window.dispatchEvent(new CustomEvent('pulse:approve-files', { detail: { content: full } }))
          setStreamingText('')

          setTerminalCards([])
      terminalCardsRef.current = []
        }
      } catch {
        await simulateResponse(prompt, addMessage, setStreamingText)
      } finally {
        setAIThinking(false)
        setStreamingText('')
      }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isImageModel = (model: string) => AI_MODELS.find((m) => m.id === model)?.category === 'images'

  const handleImageGenerate = useCallback(async (prompt: string) => {
    try {
      const res = await authFetch(apiUrl('/api/chat/generate-image'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, model: aiModel }),
      })
      const data = await res.json()
      if (data.url) {
        addMessage({ role: 'assistant', content: `![Generated Image](${data.url})\n\n*Prompt: ${prompt}*` })
      } else if (data.text) {
        addMessage({ role: 'assistant', content: data.text })
      } else {
        addMessage({ role: 'assistant', content: `⚠️ ${data.error || 'Image generation failed'}` })
      }
    } catch {
      addMessage({ role: 'assistant', content: '⚠️ Image generation request failed' })
    }
  }, [aiModel, addMessage])

  const handleSend = useCallback(async () => {
    const rawText = input.trim()
    const isPulseMode = rawText.startsWith('@pulse')
    const text = isPulseMode ? rawText.replace(/^@pulse\s*/i, '').trim() || rawText : rawText
    if (!text || isAIThinking) return
    setInput('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'

    addMessage({ role: 'user', content: text })
    persistMessage('user', text)
    setAIThinking(true)
    setStreamingText('')

    // Image model → call image generation endpoint
    // Image mode: Generate Prompt only or Generate Image
    if (imageMode) {
      try {
        // If text starts with /prompt, only generate the enhanced prompt
        if (text.startsWith('/prompt ') || text.startsWith('/p ')) {
          const rawPrompt = text.replace(/^\/prompt\s+/, '').replace(/^\/p\s+/, '')
          const res = await authFetch(apiUrl('/api/chat/generate-image'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt: rawPrompt, promptOnly: true }),
          })
          const data = await res.json()
          if (data.enhancedPrompt) {
            addMessage({ role: 'assistant', content: `**Enhanced Prompt:**\n\n${data.enhancedPrompt}\n\n_Copy this and send it to generate the image._` })
            persistMessage('assistant', data.enhancedPrompt)
          } else {
            addMessage({ role: 'assistant', content: data.error || 'Failed to generate prompt' })
          }
          setAIThinking(false)
          return
        }

        const res = await authFetch(apiUrl('/api/chat/generate-image'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: text }),
        })
        const data = await res.json()
        if (data.url) {
          const caption = data.enhancedPrompt ? `\n\n_Prompt: ${data.enhancedPrompt}_` : ''
          addMessage({ role: 'assistant', content: `[IMAGE:${data.url}]${caption}` })
          persistMessage('assistant', `[IMAGE:${data.url}]${caption}`)
        } else {
          addMessage({ role: 'assistant', content: data.text || data.error || 'Image generation failed' })
        }
      } catch (err: any) {
        addMessage({ role: 'assistant', content: `Error: ${err.message}` })
      }
      setAIThinking(false)
      return
    }

    // Agent mode - stream into chat with per-tool approval
    if (aiMode === 'agent') {
      setTerminalCards([])
      terminalCardsRef.current = []
      setSummaryText('')
      setStreamingText('')
      try {
        const res = await authFetch(apiUrl('/api/agent-stream'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ goal: text, projectName: activeRunProject || '', agentMode: localStorage.getItem('pulse-agent-mode') || 'assisted', permissions: useAgentPermissions.getState().get(activeRunProject || 'default') }),
        })
        if (!res.ok || !res.body) throw new Error(`Agent error ${res.status}`)

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let full = ''
        let buf = ''
        let currentRunId = ''
        let waitingForApproval = false
        let resolveApproval: (() => void) | null = null

        // Process events one at a time
        const processEvents = async () => {
          let currentToolOutput: string[] | null = null
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buf += decoder.decode(value, { stream: true })
            const lines = buf.split('\n')
            buf = lines.pop() ?? ''

            for (const line of lines) {
              const t = line.trim()
              if (!t.startsWith('data: ')) continue
              try {
                const evt = JSON.parse(t.slice(6))

                if (evt.type === 'ping') { currentRunId = evt.runId || ''; continue }
                if (evt.type === 'done') {
                  // Close any remaining open tool output
                  if (currentToolOutput !== null && currentToolOutput.length > 0) {
                    const finalOutput = currentToolOutput.join('\n')
                    setTerminalCards(prev => {
                      const updated = [...prev]
                      const lastNull = updated.findLastIndex(c => c.output === null)
                      if (lastNull >= 0) updated[lastNull] = { ...updated[lastNull], output: finalOutput }
                      return updated
                    })
                    terminalCardsRef.current = terminalCardsRef.current.map((c, idx) => {
                      const lastNull = terminalCardsRef.current.findLastIndex(x => x.output === null)
                      return idx === lastNull ? { ...c, output: finalOutput } : c
                    })
                    currentToolOutput = null
                  }
                  continue
                }
                if (evt.type === 'phase_complete') continue

                if (evt.type === 'phase') {
                  if (evt.label?.includes('Investigating')) full += '\n*Investigating...*\n'
                  else if (evt.phase === 'applying') full += '\n*Applying changes...*\n'
                  setStreamingText(full); continue
                }

                if (evt.type === 'tool_request') {
                  // Close any previous tool's output that didn't get a completion marker
                  if (currentToolOutput !== null && currentToolOutput.length > 0) {
                    const finalOutput = currentToolOutput.join('\n')
                    setTerminalCards(prev => {
                      const updated = [...prev]
                      if (updated.length > 0) {
                        const lastWithoutOutput = updated.findLastIndex(c => c.output === null)
                        if (lastWithoutOutput >= 0) updated[lastWithoutOutput] = { ...updated[lastWithoutOutput], output: finalOutput }
                      }
                      return updated
                    })
                    terminalCardsRef.current = terminalCardsRef.current.map((c, idx) => {
                      const lastNull = terminalCardsRef.current.findLastIndex(x => x.output === null)
                      return idx === lastNull ? { ...c, output: finalOutput } : c
                    })
                  }
                  currentToolOutput = null
                  const cardId = `tc-${Date.now()}-${Math.random().toString(36).slice(2,5)}`
                  setTerminalCards(prev => [...prev, { id: cardId, command: evt.description, output: null }])
                  terminalCardsRef.current = [...terminalCardsRef.current, { id: cardId, command: evt.description, output: null }]
                  // Safe mode: wait for Run click. Assisted/Auto: auto-run
                  const currentAgentMode = localStorage.getItem('pulse-agent-mode') || 'assisted'
                  if (currentAgentMode === 'safe') {
                    await new Promise<void>(resolve => {
                      resolveApproval = resolve
                      waitingForApproval = true
                    })
                    waitingForApproval = false
                    resolveApproval = null
                    await authFetch(apiUrl('/api/agent-stream'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approveRunId: currentRunId }) }).catch(() => {})
                  }
                  currentToolOutput = []
                  continue
                }

                if (evt.type === 'command_start') continue

                if (evt.type === 'command_output') {
                  const out = evt.output || ''
                  if (out.includes(' lines)') || out.startsWith('> scan')) continue
                  if (currentToolOutput !== null) {
                    currentToolOutput.push(out)
                    // Close the card when we get a definitive result marker
                    const isComplete = out.startsWith('✓') || out.startsWith('✗') || out.includes('Done') || out.includes('Found') || out.includes('No matches') || out.includes('rowCount') || out.includes('status') || out.includes('"ok"')
                    if (isComplete) {
                      const finalOutput = currentToolOutput.join('\n')
                      setTerminalCards(prev => {
                        const updated = [...prev]
                        if (updated.length > 0) {
                          updated[updated.length - 1] = { ...updated[updated.length - 1], output: finalOutput }
                        }
                        return updated
                      })
                      terminalCardsRef.current = terminalCardsRef.current.map((c, idx) => idx === terminalCardsRef.current.length - 1 ? { ...c, output: finalOutput } : c)
                      currentToolOutput = null
                    }
                  }
                  continue
                }

                if (evt.type === 'diff') {
                  const dl = (evt.diff || '').trim()
                  if (dl) { full += dl + '\n'; setStreamingText(full) }
                  continue
                }

                if (evt.type === 'permissions_request') {
                  // Permissions ask — auto-approve (stored per session)
                  await authFetch(apiUrl('/api/agent-stream'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approveRunId: currentRunId }) }).catch(() => {})
                  continue
                }

                if (evt.type === 'approval_required') {
                  const files = evt.files?.length ? evt.files : evt.patches?.map((p: any) => p.filePath) ?? []
                  full += '\n**Apply changes?**\n' + files.map((f: string) => '→ ' + f).join('\n') + '\n'
                  setStreamingText(full)
                  // Wait for user to click Apply
                  await new Promise<void>(resolve => {
                    resolveApproval = resolve
                    waitingForApproval = true
                    setStreamingText(full + '\n`▶ Click Apply to confirm`\n')
                  })
                  waitingForApproval = false
                  resolveApproval = null
                  await authFetch(apiUrl('/api/agent-stream'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approveRunId: currentRunId }) }).catch(() => {})
                  full += '✓ Applied\n'
                  setStreamingText(full)
                  continue
                }

                if (evt.type === 'file_patched') {
                  if (evt.path) {
                    const rawPath = evt.path.replace(/\\/g, '/')
                    const sp = `/${rawPath}`
                    console.log('[agent] file_patched:', sp, 'oldLen:', evt.oldContent?.length, 'newLen:', evt.newContent?.length)
                    useAppStore.getState().setAgentFilePatch(sp, evt.oldContent ?? '', evt.newContent ?? '')
                    // Also store without leading slash for tabs that use id without /
                    useAppStore.getState().setAgentFilePatch(rawPath, evt.oldContent ?? '', evt.newContent ?? '')
                    const store = useAppStore.getState()
                    const tab = store.openTabs.find(t => t.path === sp || t.path === rawPath || t.path.endsWith(rawPath) || rawPath.endsWith(t.path.replace(/^\//, '')))
                    console.log('[agent] found tab:', tab?.id, tab?.path)
                    if (tab && evt.newContent) {
                      store.markDirty(tab.id, evt.newContent)
                    }
                    window.dispatchEvent(new CustomEvent('pulse:agent-file-patched', { detail: { path: sp, content: evt.newContent } }))
                    window.dispatchEvent(new CustomEvent('pulse:agent-file-patched', { detail: { path: rawPath, content: evt.newContent } }))
                    window.dispatchEvent(new CustomEvent('pulse:files-changed'))
                  }
                  continue
                }

                if (evt.type === 'text') {
                  const txt = evt.text || ''
                  if (txt.match(/^(Stack:|Port:|Routes:)/)) continue
                  if (txt.trim()) {
                    full += txt
                    setStreamingText(full)
                  }
                  continue
                }

                if (evt.type === 'error') { full += '\n❌ ' + evt.message + '\n'; setStreamingText(full); continue }
                if (evt.type === 'retry') { full += '\n↻ Retry ' + evt.attempt + '\n'; setStreamingText(full); continue }
                if (evt.type === 'summary') {
                  if (evt.filesModified?.length) {
                    const sessionId = evt.runId || currentRunId
                    full += '\n✓ **Patched:** ' + evt.filesModified.map((f: string) => `[${f}](/open/${f})`).join(', ') + '\n'
                    full += '\n_Session: ' + sessionId + ' — [Undo all changes](/undo/' + sessionId + ') · [Save as Skill](/save-skill/' + sessionId + ')_\n'
                    setStreamingText(full)
                    window.dispatchEvent(new CustomEvent('pulse:files-changed'))
                  }
                  continue
                }
                if (evt.type === 'restart_backend') { full += '\n🔄 Restarting backend...\n'; setStreamingText(full); continue }
              } catch {}
            }
          }
        }

        // Store the approval resolver globally so the Apply button can trigger it
        const agentApproveHandler = () => { if (resolveApproval) resolveApproval() }
        window.addEventListener('pulse:agent-approve', agentApproveHandler)

        await processEvents()

        window.removeEventListener('pulse:agent-approve', agentApproveHandler)

        // Build final message: terminal cards + summary text together
        if (full.trim() || terminalCardsRef.current.length > 0) {
          let finalContent = ''
          if (terminalCardsRef.current.length > 0) {
            for (const card of terminalCardsRef.current) {
              finalContent += '```shell\n$ ' + card.command + '\n' + (card.output || '') + '\n```\n'
            }
          }
          if (full.trim()) finalContent += '\n' + full
          addMessage({ role: 'assistant', content: finalContent.trim() })
          persistMessage('assistant', finalContent.trim())
          setStreamingText('')
          setTerminalCards([])
          terminalCardsRef.current = []
          setSummaryText('')
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Agent failed'
        const errContent = full.trim() ? full + '\n\n❌ ' + msg : '❌ ' + msg
        addMessage({ role: 'assistant', content: errContent })
        persistMessage('assistant', errContent)
      } finally {
        setAIThinking(false)
        setStreamingText('')
      }
      return
    }


    if (imageMode) {
      await handleImageGenerate(text)
      setAIThinking(false)
      return
    }

    const contextFiles = await fetchWorkspaceFiles()

    try {
      const res = await authFetch(apiUrl('/api/chat/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            ...chatMessages.map((m) => ({ role: m.role, content: m.content })),
            { role: 'user', content: text },
          ],
          context: { mode: aiMode, model: aiModel, files: contextFiles, memory: pulseMemory },
        }),
      })

      if (!res.ok || !res.body) throw new Error(`Server error ${res.status}`)

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let full = ''
      let buf = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop() ?? ''
        for (const line of lines) {
          const t = line.trim()
          if (!t.startsWith('data: ')) continue
          const json = t.slice(6).trim()
          if (!json) continue
          try {
            const data = JSON.parse(json) as { text?: string; done?: boolean; error?: string; ping?: boolean }
            if (data.ping) continue
            if (data.error) {
              full += `\n⚠️ ${data.error}`
              setStreamingText(full)
              continue
            }
            if (data.text) { full += data.text; setStreamingText(full) }
            if (data.done) {
              addMessage({ role: 'assistant', content: full }); setLastAIResponse(full)
              persistMessage('assistant', full)
              window.dispatchEvent(new CustomEvent('pulse:approve-files', { detail: { content: full } }))
              setStreamingText('')
              full = ''
            }
          } catch (e) { /* skip malformed */ }
        }
      }
      // Commit anything remaining if server didn't send done event
      if (full.trim()) {
        addMessage({ role: 'assistant', content: full }); setLastAIResponse(full)
        persistMessage('assistant', full)
        setStreamingText('')
        window.dispatchEvent(new CustomEvent('pulse:approve-files', { detail: { content: full } }))
      }
    } catch (err) {
      const fallback = `I couldn't process that request. Please ensure the server is running.`
      addMessage({ role: 'assistant', content: fallback })
      persistMessage('assistant', fallback)
    } finally {
      setAIThinking(false)
      setStreamingText('')
    }
  }, [input, isAIThinking, chatMessages, aiMode, addMessage, setAIThinking])

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  // Voice: send a message from voice input
  const handleVoiceSend = useCallback(async (text: string) => {
    addMessage({ role: 'user', content: text })
    setAIThinking(true)
    setStreamingText('')
    const contextFiles = await fetchWorkspaceFiles()
    abortRef.current = new AbortController()

    try {
      const res = await authFetch(apiUrl('/api/chat/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abortRef.current.signal,
        body: JSON.stringify({
          messages: [
            ...useAppStore.getState().chatMessages.map((m) => ({ role: m.role, content: m.content })),
          ],
          context: { mode: aiMode, model: aiModel, files: contextFiles, memory: pulseMemory },
        }),
      })
      if (!res.ok || !res.body) throw new Error(`Server error ${res.status}`)
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let full = ''
      let buf = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop() ?? ''
        for (const line of lines) {
          const t = line.trim()
          if (!t.startsWith('data: ')) continue
          try {
            const data = JSON.parse(t.slice(6))
            if (data.text) { full += data.text; setStreamingText(full) }
            if (data.done) {
              addMessage({ role: 'assistant', content: full }); setLastAIResponse(full); speakText(full)
              if (aiMode === 'edit' || aiMode === 'agent') window.dispatchEvent(new CustomEvent('pulse:approve-files', { detail: { content: full } }))
              setStreamingText('')
              full = ''
            }
          } catch {}
        }
      }
      if (full.trim()) {
        addMessage({ role: 'assistant', content: full }); setLastAIResponse(full); speakText(full)
        if (aiMode === 'edit' || aiMode === 'agent') window.dispatchEvent(new CustomEvent('pulse:approve-files', { detail: { content: full } }))
        setStreamingText('')
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        addMessage({ role: 'assistant', content: 'Voice request failed. Try again.' })
      }
    } finally {
      setAIThinking(false)
      setStreamingText('')
      abortRef.current = null
    }
  }, [aiMode, aiModel, addMessage, setAIThinking, autoApplyFileOps, fetchWorkspaceFiles])

  // Voice: stop generation
  const handleVoiceStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
    setAIThinking(false)
    setStreamingText('')
  }, [setAIThinking])

  // Voice toggle
  const handleVoiceToggle = useCallback(() => {
  if (voiceState !== 'idle') {
    stopVoiceSession()
    stopWakeWordListening()
    setWakeActive(false)
  } else if (wakeActive) {
    stopWakeWordListening()
    setWakeActive(false)
  } else {
    startWakeWordListening({ onSend: handleVoiceSend, onStop: handleVoiceStop })
    setWakeActive(true)
  }
}, [voiceState, wakeActive, handleVoiceSend, handleVoiceStop])

  const autoResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    e.target.style.height = 'auto'
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px'
    e.target.scrollTop = e.target.scrollHeight
  }

  return (
    <div className="ai-panel">
      {/* Header */}
      <div className="ai-panel__header">
        <div className="ai-panel__title">
          <span className="ai-panel__pulse" />
          Pulse AI
          <button
            onClick={() => { setShowHistory((h) => !h); if (!showHistory) loadHistory() }}
            style={{
              marginLeft: 8, padding: '2px 8px', borderRadius: 10,
              border: '1px solid var(--border-2)', background: showHistory ? 'var(--bg-3)' : 'none',
              color: 'var(--text-1)', cursor: 'pointer', fontSize: 9,
              fontFamily: 'var(--font-mono)', fontWeight: 700,
              transition: 'all 0.12s', display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
            title="Chat history"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            History
          </button>
          <button
            onClick={newChat}
            style={{
              padding: '2px 8px', borderRadius: 10,
              border: '1px solid rgba(95,212,164,0.3)', background: 'rgba(95,212,164,0.08)',
              color: 'var(--accent-2)', cursor: 'pointer', fontSize: 9,
              fontFamily: 'var(--font-mono)', fontWeight: 700,
              transition: 'all 0.12s',
            }}
            title="New chat"
          >
            + New
          </button>
          {onOpenGoals && (
            <button
              onClick={onOpenGoals}
              style={{
                padding: '2px 8px', borderRadius: 10,
                border: '1px solid rgba(124,110,245,0.3)', background: 'rgba(124,110,245,0.1)',
                color: 'var(--accent)', cursor: 'pointer', fontSize: 9,
                fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '0.05em',
                transition: 'all 0.12s',
              }}
              title="View build roadmap"
            >
              ◎ Goals
            </button>
          )}
          <button
            onClick={() => setShowActivityLog(!showActivityLog)}
            style={{
              padding: '2px 8px', borderRadius: 10,
              border: '1px solid var(--border-2)', background: showActivityLog ? 'var(--bg-3)' : 'none',
              color: 'var(--text-1)', cursor: 'pointer', fontSize: 9,
              fontFamily: 'var(--font-mono)', fontWeight: 600,
            }}
            title="Agent activity log"
          >
            📊
          </button>
        </div>
        <div className="ai-panel__modes">
          {(['ask', 'edit', 'agent'] as AIMode[]).map((m) => (
            <button
              key={m}
              className={`ai-mode-btn ${aiMode === m ? 'ai-mode-btn--active' : ''}`}
              onClick={() => setAIMode(m)}
            >
              {m}
            </button>
          ))}
          {aiMode === 'agent' && (
            <select
              defaultValue={localStorage.getItem('pulse-agent-mode') || 'assisted'}
              onChange={(e) => localStorage.setItem('pulse-agent-mode', e.target.value)}
              style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-1)', fontSize: 9, fontFamily: 'var(--font-mono)', cursor: 'pointer' }}
              title="Safe: approve everything. Assisted: approve code changes. Auto: no approvals."
            >
              <option value="safe">🔒 Safe</option>
              <option value="assisted">🤝 Assisted</option>
              <option value="autonomous">⚡ Auto</option>
            </select>
          )}
        </div>
      </div>

      {/* Image/Code toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
        borderBottom: '1px solid var(--border)', flexShrink: 0,
      }}>
        <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>
          {imageMode ? '💼️ Image Gen' : '💻 Code/Chat'}
        </span>
        <button
          onClick={() => {
            const next = !imageMode
            setImageMode(next)
            // Auto-select first model of the new category
            const filtered = AI_MODELS.filter((m) => m.active && (next ? m.category === 'images' : m.category !== 'images'))
            if (filtered.length && !filtered.find((m) => m.id === aiModel)) {
              setAIModel(filtered[0].id)
            }
          }}
          style={{
            position: 'relative', width: 36, height: 18, borderRadius: 10, border: 'none',
            background: imageMode ? 'var(--accent)' : 'var(--bg-3)', cursor: 'pointer',
            transition: 'background 0.2s',
          }}
        >
          <span style={{
            position: 'absolute', top: 2, left: imageMode ? 20 : 2,
            width: 14, height: 14, borderRadius: '50%',
            background: imageMode ? '#fff' : 'var(--text-2)',
            transition: 'left 0.2s',
          }} />
        </button>
        <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', marginLeft: 'auto' }}>
          {imageMode ? 'Describe what you want to see' : 'Ask, edit, or build code'}
        </span>
      </div>

      {/* Model selector */}
      <div style={{ position: 'relative', flexShrink: 0 }} ref={modelPickerRef}>
        <button
          onClick={() => setShowModelPicker((p) => !p)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 14px', background: 'none', border: 'none',
            borderBottom: '1px solid var(--border)', cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-1)',
            transition: 'background 0.12s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-2)' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none' }}
        >
          <span style={{ color: 'var(--accent-2)', fontSize: 12 }}>
            {AI_MODELS.find((m) => m.id === aiModel)?.icon ?? '✦'}
          </span>
          <span>{AI_MODELS.find((m) => m.id === aiModel)?.name ?? aiModel}</span>
          <span style={{ marginLeft: 'auto', fontSize: 8, color: 'var(--text-2)' }}>
            {AI_MODELS.find((m) => m.id === aiModel)?.active ? '🟢' : '🟡'}
          </span>
          <span style={{ fontSize: 9, color: 'var(--text-2)' }}>▾</span>
        </button>

        {showModelPicker && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
            background: 'var(--bg-1)', border: '1px solid var(--border-2)',
            borderRadius: '0 0 8px 8px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            maxHeight: 320, overflowY: 'auto',
          }}>
            {AI_MODELS.filter((m) => imageMode ? m.category === 'images' : m.category !== 'images').map((m) => (
              <button
                key={m.id}
                onClick={() => {
                  const userPlan = (useAuthStore.getState().user?.plan ?? 'free') as Plan
                  if (!canUseModel(userPlan, m.id)) {
                    alert(`${m.name} requires ${getModelPlan(m.id).toUpperCase()} plan. Upgrade in Billing.`)
                    return
                  }
                  setAIModel(m.id); setShowModelPicker(false)
                }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 14px', background: aiModel === m.id ? 'rgba(124,110,245,0.1)' : 'none',
                  border: 'none', borderBottom: '1px solid var(--border)',
                  cursor: m.active ? 'pointer' : 'default',
                  opacity: m.active ? 1 : 0.5,
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  color: aiModel === m.id ? 'var(--accent)' : 'var(--text-1)',
                  transition: 'background 0.1s', textAlign: 'left',
                }}
                onMouseEnter={(e) => { if (m.active) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-3)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = aiModel === m.id ? 'rgba(124,110,245,0.1)' : 'none' }}
                
              >
                <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{m.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{m.name}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-2)', marginTop: 1 }}>{m.provider}</div>
                </div>
                <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 8, fontWeight: 600, background: m.category === 'images' ? 'rgba(255,160,60,0.12)' : m.category === 'both' ? 'rgba(124,110,245,0.12)' : 'rgba(95,212,164,0.08)', color: m.category === 'images' ? '#ffa03c' : m.category === 'both' ? 'var(--accent)' : 'var(--accent-2)' }}>
                  {m.category === 'images' ? '💼 Images' : m.category === 'both' ? '⚡ Both' : '💻 Coding'}
                </span>
                {(() => {
                  const up = (useAuthStore.getState().user?.plan ?? 'free') as Plan
                  const mp = getModelPlan(m.id)
                  const ok = canUseModel(up, m.id)
                  if (!ok) return <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 6, fontWeight: 700, fontFamily: 'var(--font-mono)', background: 'rgba(255,160,60,0.12)', color: '#ffa03c' }}>🔒 {mp.toUpperCase()}</span>
                  return <span style={{ fontSize: 8, color: 'var(--accent-2)', background: 'rgba(95,212,164,0.12)', padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>✓</span>
                })()}
                {aiModel === m.id && <span style={{ color: 'var(--accent)', fontSize: 12 }}>✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chat Tabs */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 0, padding: '0 4px',
        borderBottom: '1px solid var(--border)', flexShrink: 0,
        background: 'var(--bg-1)', overflowX: 'auto',
      }}>
        {chatTabs.map((tab, idx) => (
          <div
            key={tab.id}
            onClick={() => switchTab(idx)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '5px 10px', cursor: 'pointer',
              fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 600,
              color: idx === activeTabIdx ? 'var(--text-0)' : 'var(--text-2)',
              borderBottom: idx === activeTabIdx ? '2px solid var(--accent)' : '2px solid transparent',
              background: idx === activeTabIdx ? 'var(--bg-2)' : 'none',
              whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
              transition: 'all 0.1s',
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Chat {idx + 1}</span>
            {chatTabs.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); closeChatTab(idx) }}
                style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9, padding: '0 2px', lineHeight: 1, borderRadius: 3 }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--red)' }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-2)' }}
              >✕</button>
            )}
          </div>
        ))}
        <button
          onClick={addChatTab}
          style={{
            background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer',
            fontSize: 13, padding: '4px 8px', lineHeight: 1, flexShrink: 0,
          }}
          title="New chat tab"
        >+</button>
      </div>

      {/* Context pills */}
      <div className="ai-panel__context">
        {contextPills.map((pill) => (
          <span key={pill} className="ctx-pill">
            🔄 {pill}
            <button onClick={() => removeContextPill(pill)}>✕</button>
          </span>
        ))}
        <button className="ctx-add" onClick={() => addContextPill('newFile.ts')}>
          + context
        </button>
      </div>

      {/* History drawer */}
      {showActivityLog && (
        <div style={{ borderBottom: '1px solid var(--border)', maxHeight: 350, overflowY: 'auto', background: 'var(--bg-0)', flexShrink: 0 }}>
          <AgentActivityLog onClose={() => setShowActivityLog(false)} />
        </div>
      )}
      {showHistory && (
        <div style={{
          borderBottom: '1px solid var(--border)', maxHeight: 240, overflowY: 'auto',
          background: 'var(--bg-0)', flexShrink: 0,
        }}>
          {historyList.length === 0 && (
            <div style={{ padding: 14, fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
              No past conversations
            </div>
          )}
          {historyList.map((c) => (
            <div
              key={c.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)',
                color: convId === c.id ? 'var(--accent)' : 'var(--text-1)',
                background: convId === c.id ? 'rgba(124,110,245,0.08)' : 'none',
                borderBottom: '1px solid var(--border)',
                transition: 'background 0.08s',
              }}
              onClick={() => loadConversation(c.id)}
              onMouseEnter={(e) => { if (convId !== c.id) e.currentTarget.style.background = 'var(--bg-2)' }}
              onMouseLeave={(e) => { if (convId !== c.id) e.currentTarget.style.background = 'none' }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.title || 'Untitled'}
              </span>
              <span style={{ fontSize: 9, color: 'var(--text-2)', flexShrink: 0 }}>
                {new Date(c.updated_at).toLocaleDateString()}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); deleteConv(c.id) }}
                style={{
                  background: 'none', border: 'none', color: 'var(--text-2)',
                  cursor: 'pointer', fontSize: 10, padding: '0 4px', flexShrink: 0,
                  borderRadius: 3, transition: 'color 0.1s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--red)' }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-2)' }}
                title="Delete"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="ai-panel__messages">
        {chatMessages.length === 0 && <EmptyState mode={aiMode} />}

        {chatMessages.map((msg) => (
          msg.agentRun ? (
            <div key={msg.id} className="ai-msg ai-msg--ai">
              <AgentStreamRenderer goal={msg.agentRun.goal} frozenPhases={msg.agentRun.phases} frozenDone />
              {msg.agentRun.filesModified.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--accent-2)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
                  ✓ Patched: {msg.agentRun.filesModified.join(', ')}
                </div>
              )}
            </div>
          ) : (
            <MessageBubble key={msg.id} msg={msg} onRetry={msg.role === "user" ? undefined : () => { const lastUser = chatMessages.filter(m => m.role === "user").at(-1); if (lastUser) handleVoiceSend(lastUser.content) }} />
          )
        ))}

        {isAIThinking && !streamingText && !agentRunning && <ThinkingIndicator />}

        {agentRunning && agentGoal && (
          <div className="ai-msg ai-msg--ai">
            <AgentStreamRenderer
              goal={agentGoal}
              onComplete={(content, filesModified, phases) => {
                if (phases?.length) {
                  setAgentArchive((prev) => {
                    const next = [...prev, { goal: agentGoal, phases, filesModified: filesModified ?? [] }]
                    localStorage.setItem('pulse-agent-archive', JSON.stringify(next.slice(-10)))
                    return next
                  })
                }
                // Save agent result as assistant message for persistence
                const summary = filesModified?.length
                  ? `Agent completed: patched ${filesModified.join(', ')}`
                  : content || 'Agent completed (no changes)'
                const msg: any = { role: 'assistant', content: summary }; if (phases?.length) msg.agentRun = { goal: agentGoal, phases, filesModified: filesModified ?? [] }; addMessage(msg)
                persistMessage('assistant', summary)
                // Agent-stream patches on approval — timeline stays in chat via agentArchive
                setAgentRunning(false)
                setAgentGoal('')
                // Notify dashboard to refresh projects
                if (filesModified?.length) {
                  window.dispatchEvent(new CustomEvent('pulse:projects-changed'))
                  window.dispatchEvent(new CustomEvent('pulse:files-changed'))
                }
              }}
              onError={(msg) => {
                addMessage({ role: 'assistant', content: 'Agent error: ' + msg })
                setAgentRunning(false)
                setAgentGoal('')
              }}
            />
          </div>
        )}

        {(streamingText || terminalCards.length > 0) && (
          <div className="ai-msg ai-msg--ai">
            <div className="ai-msg__from">
              <span className="ai-msg__dot ai-msg__dot--ai" /> Pulse AI
            </div>
            <div className="ai-msg__body">
              {terminalCards.map(card => (
                <TerminalCard
                  key={card.id}
                  command={card.command}
                  output={card.output}
                  onRun={() => window.dispatchEvent(new CustomEvent('pulse:agent-approve'))}
                />
              ))}
              {summaryText && <div style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}><FormattedText text={summaryText} /></div>}
              {streamingText && <StreamingText text={streamingText.replace(/`▶ Click Apply to run`/g, '').replace(/`▶ Click Apply to confirm`/g, '')} />}
              {(streamingText && (streamingText.includes('Click Apply to run') || streamingText.includes('Click Apply to confirm'))) && (
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('pulse:agent-approve'))}
                  style={{ marginTop: 10, padding: '9px 20px', borderRadius: 8, background: 'rgba(95,212,164,0.12)', border: '1px solid rgba(95,212,164,0.35)', color: '#5fd4a4', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}
                >▶ Apply</button>
              )}
            </div>
          </div>
        )}
        {/* Pause button during agent execution */}
        {isAIThinking && aiMode === 'agent' && (
          <div style={{ padding: '4px 16px' }}>
            <button
              onClick={async () => {
                // Signal pause - abort the current stream
                setAIThinking(false)
                setStreamingText('')
                addMessage({ role: 'assistant', content: '⏸ Agent paused. Click Resume in Activity to continue.' })
              }}
              style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(245,161,87,0.3)', background: 'rgba(245,161,87,0.08)', color: '#f5a157', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}
            >
              ⏸ Pause
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Voice interaction */}
      <div className="ai-panel__voice">
        {voiceState !== 'idle' && (
          <div style={{
            padding: '6px 12px', marginBottom: 6, borderRadius: 8,
            background: 'var(--bg-2)', border: '1px solid var(--border)',
            fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)',
          }}>
            {/* Live transcript */}
            {voiceTranscript && (
              <div style={{ color: 'var(--text-0)', marginBottom: 4 }}>
                {voiceTranscript}
                {voiceInterim && <span style={{ color: 'var(--text-2)' }}> {voiceInterim}</span>}
              </div>
            )}
            {!voiceTranscript && voiceInterim && (
              <div style={{ color: 'var(--text-2)' }}>{voiceInterim}</div>
            )}
            {/* Status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: voiceState === 'listening' ? 'var(--red)'
                  : voiceState === 'processing' ? 'var(--accent-3)'
                  : 'var(--accent-2)',
                animation: voiceState === 'listening' ? 'pulse-heartbeat 1.4s infinite' : undefined,
              }} />
              <span>
                {voiceState === 'listening' && 'Listening...'}
                {voiceState === 'processing' && 'Processing...'}
                {voiceState === 'responding' && 'Responding...'}
              </span>
              {voiceState === 'listening' && voiceSilence > 500 && (
                <span style={{ marginLeft: 'auto', color: 'var(--text-2)' }}>
                  {Math.max(0, Math.ceil((2000 - voiceSilence) / 1000))}s
                </span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 9, color: 'var(--text-2)' }}>
                say "stop" to cancel · "ok" to end
              </span>
            </div>
          </div>
        )}

        

        <button
          className={`voice-btn ${voiceState !== 'idle' ? 'voice-btn--recording' : ''}`}
          onClick={handleVoiceToggle}
          disabled={!isVoiceSupported()}
        >
          <span>🎙</span>
          {voiceState !== 'idle' ? (
            <>
              <AudioOrb isListening={voiceState !== "idle" || wakeActive} audioLevel={audioLevel} />
              <span style={{ marginLeft: 'auto', fontSize: 10 }}>
                {voiceState === 'listening' ? 'Listening…' : voiceState === 'processing' ? 'Thinking…' : 'Speaking…'}
              </span>
            </>
          ) : (
            <span>{isVoiceSupported() ? (wakeActive ? 'Mic ON • say mike • click to stop' : 'Click to activate mic') : 'Voice not supported'}</span>
          )}
        </button>
      </div>

      {/* Input */}
      <div className="ai-panel__input-zone">
        <div className="ai-input-wrap">
          <ChatImageButton />
          <textarea
            ref={textareaRef}
            className="ai-input"
            placeholder={imageMode ? 'Describe the image you want…' : `${aiMode === 'ask' ? 'Ask anything…' : aiMode === 'edit' ? 'Describe the edit…' : 'Describe the task…'}`}
            value={input}
            onChange={autoResize}
            onKeyDown={handleKey}
            rows={1}
          />
          <button
            className="ai-send-btn"
            onClick={handleSend}
            disabled={!input.trim() || isAIThinking}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        <p className="ai-panel__hint">↵ send · ⇧↵ newline · ⌘⇧V voice</p>
      </div>
    </div>
  )
}

function MessageBubble({ msg, onRetry }: { msg: ChatMessage; onRetry?: () => void }) {
  const isUser = msg.role === 'user'
  const isImage = msg.content.startsWith('[IMAGE:')
  const imageUrl = isImage ? msg.content.match(/\[IMAGE:(.*?)\]/)?.[1] || null : null
  const imageCaption = isImage ? msg.content.replace(/\[IMAGE:.*?\]/, '').trim() : null
  const parts = isImage ? [] : parseMessage(msg.content)
  const [copied, setCopied] = useState(false)
  const [rating, setRating] = useState(0)
  const currentModel = useAppStore((s) => s.aiModel)
  const filesCreated = !isUser ? (msg.content.match(/\/\/ FILE:/g) || []).length : 0
  const copyAll = () => { navigator.clipboard.writeText(msg.content); setCopied(true); setTimeout(() => setCopied(false), 1500) }

  return (
    <div className={`ai-msg ${isUser ? 'ai-msg--user' : 'ai-msg--ai'}`}>
      <div className="ai-msg__from">
        <span className={`ai-msg__dot ${isUser ? 'ai-msg__dot--user' : 'ai-msg__dot--ai'}`} />
        {isUser ? 'You' : 'Pulse AI'}
        {!isUser && <span style={{ fontSize: 9, color: 'var(--text-2)', marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>{currentModel === 'auto' ? 'Auto' : (AI_MODELS.find((m) => m.id === currentModel)?.name ?? '')}</span>}
      </div>
      {!isUser && filesCreated > 0 && (
        <div style={{ display: 'flex', gap: 6, padding: '4px 10px', margin: '2px 0 6px', borderRadius: 5, background: 'rgba(95,212,164,0.06)', border: '1px solid rgba(95,212,164,0.12)', fontSize: 9, fontFamily: 'var(--font-mono)', color: '#5fd4a4' }}>
          ✓ {filesCreated} file{filesCreated > 1 ? 's' : ''} created
          <button onClick={() => { const ops = extractCodeBlocks(msg.content).map(b => parsePulseFileOp(b)).filter(Boolean); ops.forEach(op => { if (op) useAppStore.getState().undoFileChange(op.storedPath) }); }} style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(240,112,112,0.3)', color: 'var(--red)', cursor: 'pointer', fontSize: 8, padding: '1px 6px', borderRadius: 4, fontFamily: 'var(--font-mono)' }}>↩ Undo All</button>
        </div>
      )}
      <div className="ai-msg__body">
        {imageUrl ? (
          <>
            <img src={imageUrl} alt="Generated image" style={{ maxWidth: '100%', borderRadius: 8, margin: '8px 0', display: 'block' }} />
            {imageCaption && <div style={{ fontSize: 10, color: 'var(--text-2)', fontStyle: 'italic', marginTop: 4 }}>{imageCaption}</div>}
          </>
        ) : parts.map((part, i) =>
          part.type === 'code' ? (
            <CodeBlock key={i} lang={part.lang ?? ''} code={part.content} autoApplied={filesCreated > 0} />
          ) : (
            <div key={i} style={{ whiteSpace: 'pre-wrap' }}><FormattedText text={part.content} /></div>
          )
        )}
      </div>
      {!isUser && (
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <button onClick={copyAll} style={{ padding: '2px 7px', borderRadius: 4, border: '1px solid var(--border)', background: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9, fontFamily: 'var(--font-mono)' }}>{copied ? '✓ Copied' : '📋 Copy'}</button>
          {onRetry && <button onClick={onRetry} style={{ padding: '2px 7px', borderRadius: 4, border: '1px solid var(--border)', background: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9, fontFamily: 'var(--font-mono)' }}>🔄 Retry</button>}
          <button onClick={() => { setRating(1); authFetch(apiUrl('/api/training/rate'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ entryId: msg.id, rating: 1 }) }).catch(() => {}) }} style={{ padding: '2px 7px', borderRadius: 4, border: '1px solid var(--border)', background: rating === 1 ? 'rgba(95,212,164,0.15)' : 'none', color: rating === 1 ? 'var(--accent-2)' : 'var(--text-2)', cursor: 'pointer', fontSize: 9 }}>👍</button>
          <button onClick={() => { setRating(-1); authFetch(apiUrl('/api/training/rate'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ entryId: msg.id, rating: -1 }) }).catch(() => {}) }} style={{ padding: '2px 7px', borderRadius: 4, border: '1px solid var(--border)', background: rating === -1 ? 'rgba(240,112,112,0.15)' : 'none', color: rating === -1 ? 'var(--red)' : 'var(--text-2)', cursor: 'pointer', fontSize: 9 }}>👎</button>
        </div>
      )}
    </div>
  )
}

function CodeBlock({ lang, code, autoApplied }: { lang: string; code: string; autoApplied?: boolean }) {
  const [copied, setCopied] = useState(false)
  const [applied, setApplied] = useState(!!autoApplied)
  const [showDiff, setShowDiff] = useState(false)
  const { activeTabId, markDirty, openTabs, openTab, closeTab, snapshotFile } = useAppStore()

  const copy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const apply = async () => {
    // If this is a shell/tool block from the agent, dispatch approval event
    if (lang === 'shell' || lang === 'bash') {
      window.dispatchEvent(new CustomEvent('pulse:agent-approve'))
      setApplied(true)
      return
    }
    const op = parsePulseFileOp(code)

    // Handle terminal commands
    if (op && op.relPath === '__terminal__') {
      const commands = code.split('\n').slice(1).filter((l) => l.trim() && !l.trim().startsWith('//')).join(' && ')
      if (commands) {
        window.dispatchEvent(new CustomEvent('pulse:run-terminal', { detail: { commands } }))
      }
      return
    }
    // Bash/sh code blocks without FILE header = terminal commands
    if (!op && (lang === 'bash' || lang === 'sh' || lang === 'shell')) {
      const commands = code.split('\n').filter((l) => l.trim() && !l.trim().startsWith('#')).join(' && ')
      if (commands) {
        window.dispatchEvent(new CustomEvent('pulse:run-terminal', { detail: { commands } }))
      }
      return
    }
    // Diff block → patch instead of full replace
    if (lang === 'diff' || code.trim().startsWith('---')) {
      const firstLine = code.trim().split('\n')[0] ?? ''
      const fileMatch = firstLine.match(/^---\s+(.+)/)
      if (fileMatch) {
        const relPath = fileMatch[1].trim().replace(/^[.]{0,2}\//, '').replace(/^[/\\]+/, '')
        try {
          const res = await authFetch(apiUrl('/api/agent-stream/apply-patch'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filePath: relPath, diff: code.trim() }),
          })
          if (res.ok) {
            window.dispatchEvent(new CustomEvent('pulse:files-changed'))
            return
          }
        } catch {}
      }
    }

    // If the model included file headers, perform real file operations (create/update/delete).
    if (op) {
      // Snapshot before applying (for undo)
      const existingTab = openTabs.find((t) => t.path === op.storedPath)
      if (existingTab) snapshotFile(op.storedPath, existingTab.content)

      if (op.action === 'delete') {
        if (existingTab) closeTab(existingTab.id)
      } else {
        if (existingTab) {
          markDirty(existingTab.id, op.content)
        } else {
          openTab({
            id: op.storedPath,
            path: op.storedPath,
            name: op.storedPath.split('/').at(-1) ?? op.storedPath,
            language: inferLanguageFromRelPath(op.relPath),
            content: op.content,
          })
        }
      }

      // Send to VS Code extension host (real filesystem)
      const isInVsCodeWebview = window.parent && window.parent !== window
      if (isInVsCodeWebview) {
        try {
          window.parent.postMessage(
            op.action === 'delete'
              ? { type: 'deleteFile', path: op.relPath }
              : { type: 'writeFile', path: op.relPath, content: op.content },
            '*'
          )
        } catch {
          // Ignore if postMessage fails
        }
        return
      }

      // Browser mode: call the dev server filesystem API.
      // This makes create/update/delete testable with `pnpm dev` alone.
    const doRequest = async () => {
        try {
          const endpoint = op.action === 'delete' ? '/api/files/delete' : '/api/files/write'
          const res = await authFetch(apiUrl(endpoint), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              path: op.relPath,
              content: op.content,
            }),
          })

          if (!res.ok) {
            const text = await res.text().catch(() => '')
            throw new Error(`file op failed (${res.status}): ${text}`)
          }
        } catch (e) {
          // Best-effort: still applied to the in-memory tab.
          // Swallow errors so the UI doesn't break.
        }
      }

      await doRequest()
      // Notify sidebar to refresh file tree
      window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      return
    }

    // Fallback: snapshot + apply to the currently open editor tab
    if (activeTabId) {
      const tab = openTabs.find((t) => t.id === activeTabId)
      if (tab) snapshotFile(tab.path, tab.content)
      markDirty(activeTabId, code)
    }

    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'applyEdit', content: code }, '*')
      }
    } catch {
      // Ignore
    }
  }

  // Diff: find what file this code targets
  const op = parsePulseFileOp(code)
  const targetPath = op?.storedPath
  const oldContent = targetPath ? useAppStore.getState().getSnapshot(targetPath) : undefined
  const canUndo = targetPath && oldContent !== undefined

  const handleUndo = () => {
    if (targetPath) useAppStore.getState().undoFileChange(targetPath)
  }

  return (
    <div className="ai-code-block">
      <div className="ai-code-block__header">
        <span>{lang || 'code'}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {targetPath && oldContent !== undefined && (
            <button className="ai-code-block__btn" onClick={() => setShowDiff((d) => !d)}>
              {showDiff ? 'hide diff' : 'diff'}
            </button>
          )}
          {canUndo && (
            <button className="ai-code-block__btn" onClick={handleUndo} style={{ color: 'var(--accent-3)' }}>↩ undo</button>
          )}
          <button className="ai-code-block__btn" onClick={copy}>{copied ? '✓ copied' : 'copy'}</button>
          <button className={`ai-code-block__btn ${applied ? '' : 'ai-code-block__btn--apply'}`} onClick={() => { if (!applied) { apply(); setApplied(true) } }} disabled={applied} style={applied ? { opacity: 0.6, cursor: 'default' } : undefined}>{applied ? '✓ applied' : '✓ apply'}</button>
        </div>
      </div>
      {showDiff && oldContent !== undefined ? (
        <SimpleDiff oldText={oldContent} newText={op?.content ?? code} />
      ) : (
        <pre className="ai-code-block__code">{code}</pre>
      )}
    </div>
  )
}

function StreamingText({ text }: { text: string }) {
  const [displayed, setDisplayed] = useState('')
  const targetRef = useRef(text)
  const rafRef = useRef<number | null>(null)

  targetRef.current = text

  useEffect(() => {
    let i = displayed.length
    const tick = () => {
      const target = targetRef.current
      if (i < target.length) {
        // Render 3-6 chars per frame for fast typing feel
        const step = Math.min(Math.max(3, Math.floor((target.length - i) / 8)), 6)
        i = Math.min(i + step, target.length)
        setDisplayed(target.slice(0, i))
        rafRef.current = requestAnimationFrame(tick)
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [text])

  // When text shrinks (new message), reset
  useEffect(() => {
    if (text.length < displayed.length) setDisplayed(text)
  }, [text])

  const parts = parseMessage(displayed)
  const lastTextIdx = parts.map((p, i) => p.type !== 'code' ? i : -1).filter(i => i >= 0).pop() ?? -1
  return (
    <>
      {parts.map((part, i) =>
        part.type === 'code' ? (
          <CodeBlock key={part.content.slice(0, 50)} lang={part.lang ?? ''} code={part.content} />
        ) : (
          <p key={i} style={{ whiteSpace: 'pre-wrap' }}><FormattedText text={part.content} />{i === lastTextIdx && <span className="ai-cursor" />}</p>
        )
      )}
    </>
  )
}

function ThinkingIndicator() {
  return (
    <div className="ai-msg ai-msg--ai">
      <div className="ai-msg__from">
        <span className="ai-msg__dot ai-msg__dot--ai" /> Pulse AI
      </div>
      <div className="ai-thinking">
        Analyzing your codebase
        <span className="ai-thinking__dots">
          <span /><span /><span />
        </span>
      </div>
    </div>
  )
}

function VoiceWave() {
  return (
    <span className="voice-wave">
      {[0,1,2,3,4].map((i) => <span key={i} style={{ animationDelay: `${i * 0.08}s` }} />)}
    </span>
  )
}

function SpeakingOrb() {
  return (
    <div className="speaking-orb">
      <div className="speaking-orb__ring speaking-orb__ring--1" />
      <div className="speaking-orb__ring speaking-orb__ring--2" />
      <div className="speaking-orb__ring speaking-orb__ring--3" />
      <div className="speaking-orb__core" />
    </div>
  )
}

function EmptyState({ mode }: { mode: AIMode }) {
  const prompts: Record<AIMode, string[]> = {
    ask:   ['Explain how this function works', 'What does the error on line 24 mean?', 'How do I add authentication?'],
    edit:  ['Refactor this to use async/await', 'Add error handling to fetchUser', 'Extract this into a custom hook'],
    agent: ['Add a complete auth system', 'Migrate this file to TypeScript', 'Write tests for the API routes'],
  }
  const extPrompts = getInstalledPrompts()
  return (
    <div className="ai-empty">
      <p className="ai-empty__label">Try asking:</p>
      {prompts[mode].map((p) => (
        <button key={p} className="ai-empty__prompt">{p}</button>
      ))}
      {extPrompts.length > 0 && (
        <>
          <p className="ai-empty__label" style={{ marginTop: 8 }}>From extensions:</p>
          {extPrompts.map((p) => (
            <button key={p.name} className="ai-empty__prompt" title={p.prompt}>
              🤖 {p.name}
            </button>
          ))}
        </>
      )}
    </div>
  )
}

// ─── SimpleDiff ─────────────────────────────────────────────────
function SimpleDiff({ oldText, newText }: { oldText: string; newText: string }) {
  const oldLines = oldText.split('\n')
  const newLines = newText.split('\n')
  const maxLen = Math.max(oldLines.length, newLines.length)
  const lines: { type: 'same' | 'add' | 'remove'; text: string }[] = []
  for (let i = 0; i < maxLen; i++) {
    const o = oldLines[i], n = newLines[i]
    if (o === n) { if (o !== undefined) lines.push({ type: 'same', text: o }) }
    else {
      if (o !== undefined) lines.push({ type: 'remove', text: o })
      if (n !== undefined) lines.push({ type: 'add', text: n })
    }
  }
  return (
    <pre className="ai-code-block__code" style={{ fontSize: 10.5 }}>
      {lines.map((l, i) => (
        <div key={i} style={{
          background: l.type === 'add' ? 'rgba(95,212,164,0.1)' : l.type === 'remove' ? 'rgba(240,112,112,0.1)' : 'none',
          color: l.type === 'add' ? 'var(--accent-2)' : l.type === 'remove' ? 'var(--red)' : 'var(--text-1)',
          padding: '0 4px',
        }}>
          <span style={{ display: 'inline-block', width: 14, color: 'var(--text-2)', userSelect: 'none' }}>
            {l.type === 'add' ? '+' : l.type === 'remove' ? '-' : ' '}
          </span>
          {l.text}
        </div>
      ))}
    </pre>
  )
}

// ─── Helpers ────────────────────────────────────────────────────

type MsgPart = { type: 'text' | 'code'; content: string; lang?: string }

function buildUnifiedDiff(oldLines: string[], newLines: string[]): string {
  // LCS-based diff for accurate green/red highlighting
  const n = oldLines.length, m = newLines.length
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0))
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i][j] = oldLines[i-1] === newLines[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1])
    }
  }
  const result: string[] = []
  let i = n, j = m
  const stack: string[] = []
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i-1] === newLines[j-1]) {
      stack.push('  ' + oldLines[i-1]); i--; j--
    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
      stack.push('+ ' + newLines[j-1]); j--
    } else {
      stack.push('- ' + oldLines[i-1]); i--
    }
  }
  while (stack.length) result.push(stack.pop()!)
  return result.join('\n')
}

function FormattedText({ text }: { text: string }) {
  // Detect if text contains diff content
  const hasDiff = text.includes('---') || text.includes('@@ ') || text.includes('• ') || text.includes('+ ') || (text.includes('- ') && text.includes('@@'))
  let html = text
  if (hasDiff) {
    // Apply diff colors FIRST
    html = html
      .replace(/^(\+\s.+)$/gm, '<div style="background:rgba(95,212,164,0.1);color:#5fd4a4;border-left:3px solid #5fd4a4;padding:2px 8px;border-radius:3px;font-family:var(--font-mono);font-size:11px">$1</div>')
      .replace(/^(•\s*.+)$/gm, '<div style="background:rgba(240,112,112,0.1);color:#f07070;border-left:3px solid #f07070;padding:2px 8px;border-radius:3px;font-family:var(--font-mono);font-size:11px;text-decoration:line-through;opacity:0.8">$1</div>')
      .replace(/^(-\s+.+)$/gm, '<div style="background:rgba(240,112,112,0.1);color:#f07070;border-left:3px solid #f07070;padding:2px 8px;border-radius:3px;font-family:var(--font-mono);font-size:11px;text-decoration:line-through;opacity:0.8">$1</div>')
      .replace(/^(---\s.+)$/gm, '<div style="color:#a78bfa;font-family:var(--font-mono);font-size:10px;margin-top:8px">$1</div>')
      .replace(/^(@@.+)$/gm, '<div style="color:#a78bfa;background:rgba(124,110,245,0.06);border-radius:3px;padding:2px 8px;font-family:var(--font-mono);font-size:10px">$1</div>')
  }
  html = html
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--text-0)">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:var(--bg-3);padding:1px 5px;border-radius:3px;font-family:var(--font-mono);font-size:11px">$1</code>')
    .replace(/^### (.+)$/gm, '<div style="font-weight:700;color:var(--accent);margin:8px 0 4px;font-size:13px">$1</div>')
    .replace(/^## (.+)$/gm, '<div style="font-weight:700;color:var(--accent);margin:8px 0 4px;font-size:14px">$1</div>')
    .replace(/^# (.+)$/gm, '<div style="font-weight:700;color:var(--accent);margin:8px 0 4px;font-size:15px">$1</div>')
    .replace(/^\* (.+)$/gm, '<div style="padding-left:12px">&bull; $1</div>')
    .replace(/^\d+\. (.+)$/gm, '<div style="padding-left:12px">$&</div>')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width:100%;border-radius:8px;margin:8px 0;display:block" />')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:var(--accent)" target="_blank">$1</a>')
  return <span dangerouslySetInnerHTML={{ __html: html }} />
}

type PulseFileOp = {
  action: 'write' | 'delete'
  // path relative to workspace root, e.g. "src/App.tsx"
  relPath: string
  // path stored by the web editor tabs, e.g. "/src/App.tsx"
  storedPath: string
  content: string
}

function inferLanguageFromRelPath(relPath: string): string {
  const normalized = relPath.replace(/\\/g, '/')
  const last = normalized.split('/').at(-1) ?? normalized
  const parts = last.split('.')
  const ext = parts.length > 1 ? parts.at(-1)?.toLowerCase() : ''
  switch (ext) {
    case 'ts': return 'ts'
    case 'tsx': return 'tsx'
    case 'js': return 'js'
    case 'jsx': return 'jsx'
    case 'json': return 'json'
    case 'css': return 'css'
    case 'html': return 'html'
    case 'md': return 'md'
    case 'env': return 'env'
    default: return 'text'
  }
}

function normalizeRelPath(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  // Remove leading "./" or "/" so we store relative paths consistently.
  const withoutDotSlash = trimmed.replace(/^[.]{0,2}\//, '')
  const withoutLeadingSlashes = withoutDotSlash.replace(/^[/\\]+/, '')
  if (!withoutLeadingSlashes) return null
  return withoutLeadingSlashes.replace(/\\/g, '/')
}

function parsePulseFileOp(code: string): PulseFileOp | null {
  const lines = code.split(/\r?\n/)

  let action: 'write' | 'delete' = 'write'
  let relPath: string | null = null
  let lastHeaderIndex = -1

  // Parse headers only at the top of the code block.
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) {
      // Allow blank lines before headers start.
      if (lastHeaderIndex !== -1) continue
      continue
    }

    const fileMatch = line.match(/^\/\/\s*FILE\s*:\s*(.+)\s*$/i)
    const actionMatch = line.match(/^\/\/\s*ACTION\s*:\s*(delete|remove|write|update|create)\s*$/i)

    if (fileMatch) {
      relPath = normalizeRelPath(fileMatch[1])
      lastHeaderIndex = i
      continue
    }
    if (actionMatch) {
      const a = actionMatch[1].toLowerCase()
      action = a === 'delete' || a === 'remove' ? 'delete' : 'write'
      lastHeaderIndex = i
      continue
    }

    // Stop when we hit the first non-header non-empty line after headers began.
    if (lastHeaderIndex !== -1) break
    break
  }

  if (!relPath) return null
  const storedPath = `/${relPath}`
  const body = lastHeaderIndex >= 0 ? lines.slice(lastHeaderIndex + 1).join('\n') : ''
  const content = action === 'delete' ? '' : body.trimEnd()

  return { action, relPath, storedPath, content }
}

function parseMessage(text: string): MsgPart[] {
  const parts: MsgPart[] = []
  const regex = /```(\w*)\n?([\s\S]*?)```/g
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push({ type: 'text', content: text.slice(lastIndex, match.index) })
    parts.push({ type: 'code', lang: match[1], content: match[2].trim() })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) parts.push({ type: 'text', content: text.slice(lastIndex) })
  return parts.length ? parts : [{ type: 'text', content: text }]
}

function extractCodeBlocks(text: string): string[] {
  const blocks: string[] = []
  const regex = /```\w*\n?([\s\S]*?)```/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    blocks.push(match[1].trim())
  }
  return blocks
}

// Demo response when server is not running
async function simulateResponse(
  prompt: string,
  addMessage: (m: { role: 'assistant'; content: string }) => void,
  setStreaming: (t: string) => void
) {
  const response = `I can see you're asking about: "${prompt}"

Here's what I'd suggest:

\`\`\`typescript
// PULSE AI suggestion
async function handleRequest(input: string) {
  try {
    const result = await processInput(input)
    return { success: true, data: result }
  } catch (error) {
    console.error('Failed:', error)
    throw new Error(\`Request failed: \${error}\`)
  }
}
\`\`\`

Start your server (\`pnpm --filter @pulse/server dev\`) to enable real AI responses via the Claude API.`

  let streamed = ''
  for (const char of response) {
    streamed += char
    setStreaming(streamed)
    await new Promise((r) => setTimeout(r, 8))
  }
  addMessage({ role: 'assistant', content: response })
}
